import React, { useRef, useState } from "react";
import {
    Box,
    Text,
    Badge,
    Flex,
    Tooltip,
    IconButton,
} from "@chakra-ui/react";
import { MessageCircle } from "lucide-react";
import { useHistory } from "react-router-dom";
import NoDataFound from "components/NoDataGraphics/NoDataFound";

const getStatusColor = (status) => {
    switch (status) {
        case "Approved": return "green";
        case "Pending": return "yellow";
        case "Suspended": return "red";
        case "Rejected": return "orange";
        case "Deactivated": return "purple";
        default: return "gray";
    }
};

const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    date.setHours(date.getHours() + 2);
    const formattedDate = date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "long",
        year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
    });

    return `${formattedDate} • ${formattedTime} `;
};


const StatusLogs = ({ logs = [] }) => {
    const scrollRef = useRef();
    const [activeIndex, setActiveIndex] = useState(0);
    const history = useHistory();

    const handleScroll = () => {
        const scrollLeft = scrollRef.current.scrollLeft;
        const width = scrollRef.current.offsetWidth;
        const index = Math.round(scrollLeft / width);
        setActiveIndex(index);
    };

    if (!logs || logs.length === 0) {
        return (
            <NoDataFound
                message={"No reports found"}
                width="100px"
                height="100px"
                fontSize="md"
                fontWeight="500"
                textColor="gray.400"
            />
        );
    }

    return (
        <Box>
            <Flex
                ref={scrollRef}
                overflowX="auto"
                gap={4}
                onScroll={handleScroll}
                css={{
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                }}
                sx={{
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {logs.map((log, index) => (
                    <Box
                        key={log.id}
                        minW="260px"
                        flex="0 0 auto"
                        p={4}
                        border="1px solid"
                        borderColor={index === 0 ? "#071D54" : "gray.200"}
                        borderRadius="md"
                        scrollSnapAlign="start"
                        bg="white"
                    >
                        <Flex justify="space-between" align="center" mb={2}>
                            <Badge colorScheme={getStatusColor(log.status)} borderRadius="md" > {log.status} </Badge>
                            <Tooltip label="Chat with Admin" hasArrow placement="top" bg="#071D54" color="white">
                                <IconButton icon={<MessageCircle size={16} />} size="sm" variant="ghost"
                                    onClick={() => history.push(`/admin/messages?userId=${log.updated_by_user?.user_id}`)} />
                            </Tooltip> </Flex>

                        {/* Admin Info */}
                        <Text fontSize="sm" color="gray.600">
                            <strong>Updated By:</strong>{" "}
                            {log.updated_by_user?.name || "System"}
                        </Text>

                        <Text fontSize="sm" color="gray.500">
                            {log.updated_by_user?.email}
                        </Text>

                        <Text fontSize="sm" color="gray.500">
                            {log.updated_by_user?.phone_number}
                        </Text>

                        {/* Date */}
                        <Text fontSize="xs" color="gray.400" mt={1}>
                            {formatDateTime(log.updated_at)}
                        </Text>

                        {/* Reason (fixed wrapping) */}
                        {log.reason && (
                            <Tooltip label={log.reason} hasArrow>
                                <Text
                                    mt={2}
                                    fontSize="sm"
                                    color="gray.600"
                                    noOfLines={3}
                                    wordBreak="break-word"
                                    cursor="pointer"
                                >
                                    {log.reason}
                                </Text>
                            </Tooltip>
                        )}

                    </Box>
                ))}
            </Flex>

            {/* Dots Indicator */}
            <Flex justify="center" mt={3} gap={2}>
                {logs.map((_, index) => (
                    <Box
                        key={index}
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={activeIndex === index ? "#071D54" : "#ccc"}
                        transition="0.3s"
                    />
                ))}
            </Flex>
        </Box>
    );
};

export default StatusLogs;

